import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Row,
  Space,
  Spin,
  TimePicker,
  message,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import RobinPage from "../components/RobinPage";
import { SAVE_GOOGLE_MAPS_RUSH_HOURS } from "../graphql/mutations/rushHour.graphql";
import { GET_GOOGLE_MAPS_RUSH_HOURS } from "../graphql/queries/rushHour.graphql";

type RushHourRule = {
  date: string | null;
  timeFrom: string;
  timeTo: string;
  marginMinutes: number;
};

type RushHourConfigResponse = {
  getGoogleMapsRushHours?: {
    id: string | number;
    rules: RushHourRule[];
    createdAt: string;
    updatedAt: string;
  } | null;
};

type SaveRushHourConfigResponse = {
  saveGoogleMapsRushHours: {
    id: string | number;
    rules: RushHourRule[];
  };
};

type SaveRushHourConfigVariables = {
  saveGoogleMapsRushHoursDto: {
    rules: RushHourRule[];
  };
};

type RushHourRuleFormValue = {
  date?: Dayjs | null;
  timeFrom?: Dayjs | null;
  timeTo?: Dayjs | null;
  marginMinutes?: number | null;
};

type RushHourConfigFormValues = {
  rules: RushHourRuleFormValue[];
};

const DEFAULT_RULE: RushHourRuleFormValue = {
  date: null,
  timeFrom: dayjs("12:00", "HH:mm"),
  timeTo: dayjs("15:00", "HH:mm"),
  marginMinutes: 5,
};

function RushHourConfig() {
  const { t } = useTranslation();
  const [form] = Form.useForm<RushHourConfigFormValues>();
  const [messageApi, contextHolder] = message.useMessage();

  const { data, loading } = useQuery<RushHourConfigResponse>(
    GET_GOOGLE_MAPS_RUSH_HOURS,
    {
      fetchPolicy: "network-only",
    },
  );

  const [saveGoogleMapsRushHours, { loading: saving }] = useMutation<
    SaveRushHourConfigResponse,
    SaveRushHourConfigVariables
  >(SAVE_GOOGLE_MAPS_RUSH_HOURS, {
    onCompleted: () => {
      messageApi.success(t("label.rush_hour_config.saved"));
    },
    onError: (error) => {
      messageApi.error(error.message || t("message.error.generic"));
    },
  });

  useEffect(() => {
    const rules = data?.getGoogleMapsRushHours?.rules;

    if (!rules?.length) {
      form.setFieldsValue({ rules: [DEFAULT_RULE] });
      return;
    }

    form.setFieldsValue({
      rules: rules.map((rule) => ({
        date: rule.date ? dayjs(rule.date, "YYYY-MM-DD") : null,
        timeFrom: dayjs(rule.timeFrom, "HH:mm"),
        timeTo: dayjs(rule.timeTo, "HH:mm"),
        marginMinutes: rule.marginMinutes,
      })),
    });
  }, [data, form]);

  const handleSubmit = async (values: RushHourConfigFormValues) => {
    const rules = (values.rules ?? []).map((rule) => ({
      date: rule.date ? rule.date.format("YYYY-MM-DD") : null,
      timeFrom: rule.timeFrom?.format("HH:mm") ?? "",
      timeTo: rule.timeTo?.format("HH:mm") ?? "",
      marginMinutes: Number(rule.marginMinutes ?? 0),
    }));

    await saveGoogleMapsRushHours({
      variables: {
        saveGoogleMapsRushHoursDto: {
          rules,
        },
      },
      refetchQueries: [{ query: GET_GOOGLE_MAPS_RUSH_HOURS }],
      awaitRefetchQueries: true,
    });
  };

  const createEmptyRule = (): RushHourRuleFormValue => ({
    date: null,
    timeFrom: null,
    timeTo: null,
    marginMinutes: null,
  });

  return (
    <RobinPage title={t("label.menu.rush_hour_config")}>
      {contextHolder}
      <Card>
        <Spin spinning={loading}>
          <Form<RushHourConfigFormValues>
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ rules: [DEFAULT_RULE] }}
          >
            <Form.List name="rules">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name }, index) => (
                    <Row
                      key={key}
                      gutter={[16, 16]}
                      style={{
                        marginBottom: 16,
                        paddingBottom: 16,
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <Col span={24}>
                        <Space>{`${t("label.rush_hour_config.rule")} ${index + 1}`}</Space>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          name={[name, "date"]}
                          label={t("label.rush_hour_config.date")}
                        >
                          <DatePicker
                            allowClear
                            format="YYYY-MM-DD"
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          name={[name, "timeFrom"]}
                          label={t("label.rush_hour_config.time_from")}
                          rules={[
                            {
                              required: true,
                              message: t(
                                "message.validation.required.rush_hour_time_from",
                              ),
                            },
                          ]}
                        >
                          <TimePicker
                            format="HH:mm"
                            minuteStep={5}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          name={[name, "timeTo"]}
                          label={t("label.rush_hour_config.time_to")}
                          dependencies={[["rules", name, "timeFrom"]]}
                          rules={[
                            {
                              required: true,
                              message: t(
                                "message.validation.required.rush_hour_time_to",
                              ),
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value: Dayjs | null | undefined) {
                                if (!value) {
                                  return Promise.resolve();
                                }

                                const timeFrom = getFieldValue([
                                  "rules",
                                  name,
                                  "timeFrom",
                                ]) as Dayjs | null | undefined;

                                if (!timeFrom || value.isAfter(timeFrom)) {
                                  return Promise.resolve();
                                }

                                return Promise.reject(
                                  new Error(
                                    t(
                                      "message.validation.rush_hour_time_order",
                                    ),
                                  ),
                                );
                              },
                            }),
                          ]}
                        >
                          <TimePicker
                            format="HH:mm"
                            minuteStep={5}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={5}>
                        <Form.Item
                          name={[name, "marginMinutes"]}
                          label={t("label.rush_hour_config.margin_minutes")}
                          rules={[
                            {
                              required: true,
                              message: t(
                                "message.validation.required.rush_hour_margin_minutes",
                              ),
                            },
                          ]}
                        >
                          <InputNumber
                            min={0}
                            precision={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        xs={24}
                        md={4}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          disabled={fields.length === 1}
                        >
                          {t("button.delete")}
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add(createEmptyRule())}
                    >
                      {t("button.add_rush_hour_rule")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {t("button.save")}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </RobinPage>
  );
}

export default RushHourConfig;
